'use client'

import { useId, useState } from 'react'

import { LuX } from 'react-icons/lu'

import { cn } from '@/core/utils/common'

import type { KeyboardEvent } from 'react'

type TagInputProps = {
  value: string[]
  onChange: (tags: string[]) => void
  max?: number
  label?: string
  error?: string
  placeholder?: string
  className?: string
  /** 칩 하나의 최대 글자 수 */
  maxLength?: number
  /** 보이는 label 이 없을 때 입력칸의 접근 가능한 이름 */
  ariaLabel?: string
  /** 순서가 의미 있는 목록 — 칩 앞에 번호, 마우스로 끌어 순서 바꾸기. 터치 · 키보드는 지우고 다시 넣어 바꾼다 */
  ordered?: boolean
}

export default function TagInput({
  value,
  onChange,
  max,
  label,
  error,
  placeholder = '입력 후 Enter',
  className,
  maxLength,
  ariaLabel,
  ordered = false,
}: TagInputProps) {
  const [input, setInput] = useState('')
  const [dragFrom, setDragFrom] = useState<number | null>(null)
  const inputId = useId()
  const errorId = error ? `${inputId}-error` : undefined

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim()
    if (!trimmedTag) return
    if (value.includes(trimmedTag)) return
    if (max && value.length >= max) return

    onChange([...value, trimmedTag])
    setInput('')
  }

  const removeTag = (index: number) => {
    onChange(value.filter((_, tagIndex) => tagIndex !== index))
  }

  const move = (from: number, to: number) => {
    if (from === to) return
    const next = [...value]
    const [tag] = next.splice(from, 1)
    next.splice(to, 0, tag)
    onChange(next)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // 한글 조합 중 Enter 는 글자 확정용 — 여기서 넣으면 마지막 글자가 반쪽으로 들어간다
    if (event.nativeEvent.isComposing) return
    if (event.key === 'Enter') {
      event.preventDefault()
      addTag(input)
    }

    if (event.key === 'Backspace' && !input && value.length > 0) {
      removeTag(value.length - 1)
    }
  }

  return (
    <div className={cn(className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="mb-2 block text-c14 font-medium text-foreground"
        >
          {label}
        </label>
      )}
      <div
        className={cn(
          'flex min-h-11 flex-wrap items-center gap-2 rounded-field border',
          'border-transparent bg-surface-raised px-4 py-2 transition-colors',
          'focus-within:border-point',
          error && 'border-error focus-within:border-error'
        )}
        role="group"
      >
        {value.map((tag, index) => (
          <span
            key={tag}
            draggable={ordered}
            onDragStart={ordered ? () => setDragFrom(index) : undefined}
            onDragOver={ordered ? (event) => event.preventDefault() : undefined}
            onDrop={
              ordered
                ? () => {
                    if (dragFrom !== null) move(dragFrom, index)
                    setDragFrom(null)
                  }
                : undefined
            }
            onDragEnd={ordered ? () => setDragFrom(null) : undefined}
            className={cn(
              'inline-flex min-w-0 max-w-full items-center gap-1 rounded-full bg-surface-strong',
              'py-1 pr-1 pl-2.5 text-c13 text-foreground animate-[chip-in_150ms_ease-out]',
              ordered && 'cursor-grab active:cursor-grabbing',
              dragFrom === index && 'opacity-50'
            )}
          >
            {ordered && (
              <span className="text-c12 font-semibold text-description tabular-nums">
                {index + 1}
              </span>
            )}
            <span className="min-w-0 whitespace-normal break-words">{tag}</span>
            <button
              type="button"
              onClick={() => removeTag(index)}
              aria-label={`${tag} 태그 삭제`}
              data-focus-appearance="fill"
              className={cn(
                'flex size-5 flex-none items-center justify-center rounded-full text-description',
                'transition-colors hover:bg-border hover:text-foreground focus:outline-none',
                'focus-visible:bg-border focus-visible:text-foreground'
              )}
            >
              <LuX aria-hidden="true" className="size-3" />
            </button>
          </span>
        ))}
        <input
          id={inputId}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={maxLength}
          aria-label={label ? undefined : ariaLabel}
          disabled={!!max && value.length >= max}
          placeholder={value.length === 0 ? placeholder : ''}
          aria-invalid={Boolean(error)}
          aria-describedby={errorId}
          className={cn(
            'min-h-8 min-w-[120px] flex-1 bg-transparent text-c15 outline-none',
            'placeholder:text-description disabled:cursor-not-allowed'
          )}
        />
      </div>
      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-2 text-c12 leading-5 text-error"
        >
          {error}
        </p>
      )}
    </div>
  )
}
